// Triagly SDK Main Entry Point

import { TriaglyConfig, FeedbackData } from './types';
import { FeedbackWidget } from './ui';
import { TriaglyAPI } from './api';
import { collectMetadata, captureScreenshot, RateLimiter, ConsoleLogger } from './utils';

export class Triagly {
  private config: TriaglyConfig;
  private widget: FeedbackWidget;
  private api: TriaglyAPI;
  private rateLimiter: RateLimiter;
  private consoleLogger: ConsoleLogger | null = null;

  constructor(config: TriaglyConfig) {
    // Handle backward compatibility
    let publishableKey = config.publishableKey;
    if (!publishableKey && config.projectId) {
      console.warn(
        'Triagly: projectId is deprecated. Please use publishableKey instead. ' +
        'See migration guide: https://docs.triagly.com/sdk/migration'
      );
      publishableKey = config.projectId;
    }

    if (!publishableKey) {
      throw new Error('Triagly: publishableKey is required. Get yours at https://triagly.com/dashboard');
    }

    this.config = {
      theme: 'auto',
      position: 'bottom-right',
      buttonShape: 'rounded',
      buttonText: '🐛 Feedback',
      placeholderText: 'Describe what happened...',
      successMessage: 'Feedback sent successfully!',
      errorMessage: 'Failed to send feedback. Please try again.',
      captureConsole: true,
      consoleLogLimit: 50,
      consoleLogLevels: ['error', 'warn'],
      ...config,
      publishableKey,
    };

    this.api = new TriaglyAPI(
      this.config.publishableKey,
      this.config.apiUrl,
      this.config.getToken,
      this.config.turnstileSiteKey
    );
    this.widget = new FeedbackWidget(this.config);
    this.rateLimiter = new RateLimiter(this.config.publishableKey, 3, 5 * 60 * 1000);

    // Initialize console logger if enabled
    if (this.config.captureConsole !== false) {
      this.consoleLogger = new ConsoleLogger(
        this.config.consoleLogLimit,
        this.config.consoleLogLevels
      );
      this.consoleLogger.start();
    }

    this.init();
  }

  /**
   * Initialize the SDK
   */
  private init(): void {
    // Initialize widget
    this.widget.init();

    // Listen for form submissions
    document.addEventListener('triagly:submit', (e: Event) => {
      const customEvent = e as CustomEvent;
      this.handleSubmit(customEvent.detail);
    });
  }

  /**
   * Handle feedback submission
   */
  private async handleSubmit(data: any): Promise<void> {
    try {
      // Check rate limit
      if (!this.rateLimiter.canProceed()) {
        const resetTime = Math.ceil(this.rateLimiter.getTimeUntilReset() / 1000 / 60);
        throw new Error(`Rate limit exceeded. Please try again in ${resetTime} minute(s).`);
      }

      // Collect metadata
      const metadata = collectMetadata(this.config.metadata);

      // Capture screenshot if requested
      let screenshot: string | null = null;
      if (data.includeScreenshot) {
        screenshot = await captureScreenshot();
      }

      // Prepare feedback data
      const feedbackData: FeedbackData = {
        title: data.title,
        description: data.description,
        reporterEmail: data.reporterEmail,
        screenshot: screenshot || undefined,
        consoleLogs: this.consoleLogger?.getLogs(),
      };

      // Submit to API with Turnstile token if provided
      const response = await this.api.submitFeedback(
        feedbackData,
        metadata,
        data.turnstileToken
      );

      // Record rate limit attempt
      this.rateLimiter.recordAttempt();

      // Dispatch success event for UI layer
      document.dispatchEvent(new CustomEvent('triagly:success', {
        detail: { feedbackId: response.id }
      }));

      // Call success callback
      if (this.config.onSuccess) {
        this.config.onSuccess(response.id);
      }

      console.log('Feedback submitted successfully:', response.id);
    } catch (error) {
      console.error('Failed to submit feedback:', error);

      // Dispatch error event for UI layer
      document.dispatchEvent(new CustomEvent('triagly:error', {
        detail: error
      }));

      // Call error callback
      if (this.config.onError && error instanceof Error) {
        this.config.onError(error);
      }

      throw error;
    }
  }

  /**
   * Programmatically open the feedback widget
   */
  open(): void {
    this.widget.open();
  }

  /**
   * Programmatically close the feedback widget
   */
  close(): void {
    this.widget.close();
  }

  /**
   * Submit feedback programmatically without UI
   * Note: When Turnstile is enabled, you must use the widget UI or provide a token
   */
  async submit(data: FeedbackData, turnstileToken?: string): Promise<void> {
    // Require Turnstile token if configured
    if (this.config.turnstileSiteKey && !turnstileToken) {
      throw new Error(
        'Turnstile verification required. When Turnstile is enabled, you must:\n' +
        '1. Use the widget UI (triagly.open()), or\n' +
        '2. Implement Turnstile in your form and pass the token: triagly.submit(data, token)'
      );
    }

    const metadata = collectMetadata(this.config.metadata);

    // Include console logs if available and not already provided
    if (!data.consoleLogs && this.consoleLogger) {
      data.consoleLogs = this.consoleLogger.getLogs();
    }

    await this.api.submitFeedback(data, metadata, turnstileToken);
    this.rateLimiter.recordAttempt();
  }

  /**
   * Destroy the SDK instance
   */
  destroy(): void {
    this.widget.destroy();
    this.consoleLogger?.stop();
    document.removeEventListener('triagly:submit', () => {});
  }
}

// Export types
export * from './types';

// Auto-initialize if config is in window
if (typeof window !== 'undefined') {
  const globalConfig = (window as any).TriaglyConfig;
  if (globalConfig) {
    (window as any).triagly = new Triagly(globalConfig);
  }
}

// Default export
export default Triagly;
